import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'multipart/form-data'
  }
});

export const DocumentUpload = ({ clientId, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);

    try {
      await axios.post(`${API}/documents/upload`, formData, getAuthHeaders());
      toast.success('Documento enviado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle data-testid="upload-modal-title">Upload de Documento</DialogTitle>
          <DialogDescription>Envie um documento PDF ou JPEG</DialogDescription>
        </DialogHeader>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
          data-testid="dropzone"
        >
          <input {...getInputProps()} data-testid="file-input" />
          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <p className="font-medium" data-testid="selected-file-name">{file.name}</p>
            </div>
          ) : (
            <div>
              <p className="font-medium mb-1">Clique ou arraste um arquivo</p>
              <p className="text-sm text-muted-foreground">PDF ou JPEG (máx. 10MB)</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 btn-transition"
            data-testid="cancel-upload-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || loading}
            className="flex-1 btn-transition"
            data-testid="confirm-upload-button"
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUpload;